/**
 * The social feed: what the people in your life (and a few local accounts) post, day by day, and your own posts
 * among them. Posts come from the simulation (who you know, how close you are, where they live), not from a
 * random wall of strangers; liking and commenting are small acts that move closeness. The author can be an NPC,
 * an account (a city page, an agency) or, once there are other players, a player: the feed doesn't care which.
 */
import type { LifeState } from './life.ts';
import { Rng } from './rng.ts';
import { homeCountry, type Country } from './travel.ts';
import { postPhoto, socialOf, SONGS, NPC_REACTIONS, type Comment, type Song } from './social.ts';
import type { LifeEvent } from './life.ts';

export interface FeedAuthor {
  kind: 'npc' | 'account' | 'player' | 'you';
  name: string;
  handle: string;
  relation?: string;
  /** a real player: their id (likes and comments go back to them) */
  id?: string;
  /** a real player's @username (to look them up: their page, follow, add, message) */
  username?: string;
}
export interface FeedPost {
  id: string;
  t: number;
  author: FeedAuthor;
  /** an asset path (feed/*.jpg) or 'photo:<id>' (a real capture: yours, or a shoot of you) */
  img: string;
  /** a carousel: more pictures after the first (same forms as img) */
  more?: string[];
  /** a reel: the clip's URL (img is its first frame) */
  video?: string;
  caption: string;
  place: string;
  likes: number;
  liked?: boolean;
  comments: Comment[];
  /** archived: the game's made-up likes and comments on it (NPC_REACTIONS) */
  npcLikes?: number;
  npcComments?: Comment[];
  /** reposted from someone else's post (an agency posting your shoot, you resharing it) */
  repostOf?: { author: string; handle: string };
  /** you're in it (an agency's post of your shoot): you can reshare it */
  tagsYou?: boolean;
  /** the few seconds of music on it */
  song?: Song | null;
  reshared?: boolean;
  /** a real player's post: its id on their profile */
  ref?: string;
  /** from For you (any player's, whoever they are to you), and when it was really posted (ms) */
  global?: boolean;
  at?: number;
}
export interface FeedState { posts: FeedPost[]; day: number; commented: string[] }

/** the photos people post, by country: their streets and the sights (feed/<hood>-<n>.jpg, made by tools/feed.mjs) */
const SHOTS: Record<Country, [string, number, string][]> = {
  nigeria: [['lower', 3, 'Agege'], ['middle', 3, 'Surulere'], ['upper', 3, 'Lekki']],
  france: [['paris', 3, 'Paris'], ['village', 2, 'Provence']],
  uae: [['dubai', 3, 'Downtown Dubai'], ['marina', 3, 'Dubai Marina'], ['deira', 2, 'Old Dubai']],
  italy: [['florence', 3, 'Florence'], ['rome', 3, 'Rome'], ['tuscany', 2, 'Tuscany']],
  usa: [['nyc', 3, 'New York'], ['sf', 3, 'San Francisco'], ['chicago', 3, 'Chicago']],
  china: [['shanghai', 2, 'Shanghai'], ['beijing', 2, 'Beijing'], ['greatwall', 2, 'Mutianyu']],
  japan: [['tokyo', 3, 'Tokyo'], ['kyoto', 2, 'Kyoto'], ['fuji', 2, 'Lake Kawaguchi']],
};
const CAPTIONS: Record<Country, string[]> = {
  nigeria: ['Sunday vibes ☀️', 'Lagos, I love you even when you stress me', 'Out and about 🚶', 'Traffic was madness but we move', 'Soft life loading…', 'No be small thing o', 'Grateful 🙏', 'Weekend mode'],
  france: ['Une belle journée ☀️', 'Paris, toujours', 'Petite balade', 'Apéro time 🍷', 'Le weekend enfin', 'Ma ville ❤️', 'Pause café ☕'],
  uae: ['Dubai nights ✨', 'Brunch o’clock 🥂', 'Sunset at the Marina 🌅', 'Habibi come to Dubai', 'Too hot to function 🥵', 'Karak break ☕', 'Weekend mode'],
  italy: ['Che bella giornata ☀️', 'Firenze ❤️', 'Dolce vita', 'Aperitivo time 🍹', 'Finalmente weekend', 'La mia città', 'Pausa caffè ☕'],
  usa: ['Golden hour hits different', 'City never sleeps 🌃', 'Weekend reset', 'Main character moment', 'Grateful for days like this', 'Out here 📍', 'Coffee first ☕'],
  china: ['周末 (weekend) ☀️', 'City walk 🚶', 'Finally a day off', '好天气 (lovely weather)', 'Night lights ✨', 'Coffee break ☕', '打卡 (been here) 📍'],
  japan: ['いい天気 (nice weather) ☀️', 'Weekend walk 🚶', 'Finally Friday 🍻', 'Evening light ✨', 'Little things', 'Day off 📍', 'Coffee time ☕'],
};
/** a couple of local accounts per country (they post every day; they're how the city feels on your phone) */
const ACCOUNTS: Record<Country, [string, string, number | null][]> = { // (the last: which of the country's places it posts from; null: anywhere)
  nigeria: [['Lagos Daily', 'lagosdaily', null], ['Eko Eats', 'ekoeats', 1]],
  france: [['Paris Je T\'aime', 'parisjetaime', 0], ['Petits Plats', 'petitsplats', null]],
  uae: [['Dubai Eats Daily', 'dubaieatsdaily', 0], ['Skyline Stories', 'skylinestories', 1]],
  italy: [['Firenze Segreta', 'firenzesegreta', 0], ['Toscana a Tavola', 'toscanaatavola', 1]],
  usa: [['NYC Now', 'nycnow', 0], ['Bay Area Views', 'bayareaviews', 1]],
  china: [['Shanghai Walks', 'shanghaiwalks', 0], ['Beijing Eats', 'beijingeats', 1]],
  japan: [['Tokyo Snaps', 'tokyo_snaps', 0], ['Kyoto Days', 'kyotodays', 1]],
};
const COMMENTS: Record<Country, string[]> = {
  nigeria: ['Fine!! 😍', 'Where is this?', 'Enjoy jare', 'Omo 🔥', 'Take me with you next time'],
  france: ['Trop beau 😍', 'Magnifique!', 'Profite bien', 'J\'adore', 'Next time take me!'],
  uae: ['Mashallah 😍', 'So beautiful!', 'Enjoy habibi', 'Goals 🔥', 'Next time take me!'],
  italy: ['Che bello 😍', 'Stupendo!', 'Goditela', 'Bellissima', 'Next time take me!'],
  usa: ['Gorgeous 😍', 'Wait where is this??', 'Love this', 'Obsessed', 'Take me next time!'],
  china: ['好美 (so pretty) 😍', 'Where is this?', 'Enjoy!', '太棒了 (amazing) 🔥', 'Take me next time!'],
  japan: ['きれい (beautiful) 😍', 'Where is this?', 'Enjoy!', 'すごい (amazing) 🔥', 'Take me next time!'],
};

export function feedOf(s: LifeState): FeedState {
  s.feed ??= { posts: [], day: -1, commented: [] };
  return s.feed;
}

const handleOf = (name: string, r: Rng) => `${name.toLowerCase().replace(/[^a-zÀ-ɏ]+/g, '.').replace(/^\.|\.$/g, '')}${r.pick(['', '_', String(r.int(1, 99))])}`;

/** A new day's posts: some of the people you know post (the closer, the more you see them), and the local pages. */
export function feedDay(s: LifeState, day: number) {
  const f = feedOf(s);
  if (f.day >= day) return;
  f.day = day;
  const c = homeCountry(s), r = new Rng((s.seed ^ Math.imul(day + 1, 0x7f4a7c15)) >>> 0);
  const used = new Set<string>();
  const shot = (only: number | null = null) => {
    // a picture nobody else posted today (if there's one left)
    for (let k = 0; k < 8; k++) {
      const [hood, n, place] = only === null ? r.pick(SHOTS[c]) : SHOTS[c][only];
      const img = `feed/${hood}-${r.int(0, n - 1)}.jpg`;
      if (!used.has(img) || k === 7) { used.add(img); return { img, place }; }
    }
    return { img: `feed/${SHOTS[c][0][0]}-0.jpg`, place: SHOTS[c][0][2] };
  };
  const at = () => day * 1440 + r.int(8 * 60, 22 * 60);
  for (const p of s.contacts) {
    if (!r.chance(0.1 + p.closeness / 400)) continue;
    const { img, place } = shot();
    const id = `f${day}-${p.name.replace(/\s+/g, '')}`;
    f.posts.push({ id, t: at(), author: { kind: 'npc', name: p.name, handle: handleOf(p.name, new Rng([...p.name].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7))), relation: p.relation }, img, caption: r.pick(CAPTIONS[c]), place, likes: NPC_REACTIONS ? r.int(8, 60) + Math.round(p.closeness / 3) : 0, comments: [] });
  }
  for (const [name, handle, from] of ACCOUNTS[c]) {
    if (!r.chance(0.7)) continue;
    const { img, place } = shot(from);
    f.posts.push({ id: `f${day}-${handle}`, t: at(), author: { kind: 'account', name, handle }, img, caption: `${place} today. ${r.pick(CAPTIONS[c])}`, place, likes: NPC_REACTIONS ? r.int(400, 4000) : 0, comments: [] });
  }
  // a model's agency posts its other faces too (rendered shoots: feed/shoot-<n>.jpg)
  if (s.job?.id === 'model' && r.chance(0.6)) {
    const agency = s.job.place.split(',')[0];
    f.posts.push({ id: `f${day}-agency`, t: at(), author: { kind: 'account', name: agency.split(',')[0], handle: agencyHandle(agency) }, img: `feed/shoot-${r.int(0, 5)}.jpg`, caption: r.pick(['New faces 📸', 'From today\'s set ✨', 'Campaign drop 🔥', 'Behind the lens 🎞️']), place: agency, likes: NPC_REACTIONS ? r.int(300, 2500) : 0, comments: [] });
  }
  // about half of them put a song on it (from the post's own id, so the day's other picks don't shift);
  // Nigerians lean Afro
  for (const p of f.posts) if (p.song === undefined) {
    const h = new Rng([...p.id].reduce((x, ch) => (x * 31 + ch.charCodeAt(0)) >>> 0, 11));
    p.song = !h.chance(0.45) ? null : c === 'nigeria' && h.chance(0.5) ? h.pick(SONGS.filter((x) => x.genre === 'afro')) : h.pick(SONGS);
  }
  f.posts.sort((a, b) => b.t - a.t);
  // (the newest 80 kept; never one you liked or commented on: your comment would go with it)
  if (f.posts.length > 80) { const keep = f.posts.filter((p, i) => i < 80 || p.liked || p.comments.some((c) => c.mine)); f.posts = keep.slice(0, 120); }
}

/** Everything in your feed up to now (theirs and yours), newest first. */
export function feed(s: LifeState): FeedPost[] {
  const soc = socialOf(s);
  const mine: FeedPost[] = soc.posts.map((p) => ({ id: p.id, t: p.t, author: { kind: 'you', name: s.card.name, handle: soc.handle }, img: `photo:${p.id}`, more: p.more?.map((m) => `photo:${m}`), caption: p.caption, place: p.place, likes: p.likes, comments: p.comments, repostOf: p.repostOf, song: p.song }));
  return [...feedOf(s).posts.filter((p) => p.t <= s.minutes), ...mine].sort((a, b) => b.t - a.t);
}

/** Someone's page: who they are to you, and what they've posted. */
export function profile(s: LifeState, name: string): { author: FeedAuthor | null; posts: FeedPost[] } {
  const posts = feed(s).filter((p) => p.author.name === name);
  return { author: posts[0]?.author ?? null, posts };
}

/** Like (or unlike) a post. Liking a friend's post is noticed: a little closer, once per post. */
export function likePost(s: LifeState, id: string): FeedPost | null {
  const p = feedOf(s).posts.find((x) => x.id === id);
  if (!p) return null;
  p.liked = !p.liked;
  p.likes += p.liked ? 1 : -1;
  const who = p.author.kind === 'npc' ? s.contacts.find((c) => c.name === p.author.name) : null;
  if (who && p.liked && !feedOf(s).commented.includes(`like:${id}`)) { feedOf(s).commented.push(`like:${id}`); who.closeness = Math.min(100, who.closeness + 0.5); }
  s.needs.social = Math.min(100, s.needs.social + 0.5);
  return p;
}

/** Comment on a post. A friend notices (closer), and usually answers in the thread. */
export function commentOn(s: LifeState, id: string, text: string): FeedPost | null {
  const p = feedOf(s).posts.find((x) => x.id === id);
  const body = text.trim().slice(0, 200);
  if (!p || !body) return null;
  p.comments.push({ name: socialOf(s).handle, text: body, t: s.minutes, mine: true });
  const who = p.author.kind === 'npc' ? s.contacts.find((c) => c.name === p.author.name) : null;
  const f = feedOf(s);
  if (who && !f.commented.includes(id)) { f.commented.push(id); who.closeness = Math.min(100, who.closeness + 2); }
  const r = new Rng((s.seed ^ Math.imul(s.minutes + p.comments.length, 0x2c1b3c6d)) >>> 0);
  if (NPC_REACTIONS && p.author.kind !== 'you' && r.chance(who ? 0.4 + who.closeness / 200 : 0.15)) p.comments.push({ name: p.author.handle, text: r.pick(['❤️', '😂😂', 'Thank you!', '🙏', 'Haha yes!', '🥰']), t: s.minutes + r.int(3, 40) });
  s.needs.social = Math.min(100, s.needs.social + 1);
  if (f.commented.length > 200) f.commented.splice(0, 50);
  return p;
}

const agencyHandle = (agency: string) => agency.split(',')[0].toLowerCase().replace(/[^a-z]+/g, '').slice(0, 20);

/**
 * After a shoot: in a few hours the agency posts the best photo, tagging you, and messages you to say so.
 * (Its other models' shoots are in its feed too: you're one face among many until you're not.)
 */
export function agencyPost(s: LifeState, photoId: string, agency: string, quality: number) {
  const f = feedOf(s), soc = socialOf(s), handle = agencyHandle(agency);
  const r = new Rng((s.seed ^ Math.imul(s.minutes + 7, 0x5bd1e995)) >>> 0);
  const t = s.minutes + r.int(150, 260);
  f.posts.push({ id: `ag-${photoId}`, t, author: { kind: 'account', name: agency.split(',')[0], handle }, img: `photo:${photoId}`, place: agency.split(',')[0],
    caption: `${r.pick(['New faces 📸', 'Fresh from the studio ✨', 'On set today 🎞️', 'This week\'s shoot 🔥'])} Featuring @${soc.handle}`,
    likes: NPC_REACTIONS ? Math.round(300 + quality * 1800 + r.int(0, 300)) : 0, comments: [], tagsYou: true });
  f.posts.sort((a, b) => b.t - a.t);
  s.inbox.push({ at: t + 1, from: agency.split(',')[0], text: `We just posted your shoot! 📸 Have a look on Social, and reshare it if you like it.` });
}

/** Reshare an agency's post of you to your own page (with credit). The agency notices and reposts you: a boost. */
export function reshare(s: LifeState, id: string): LifeEvent[] {
  const p = feedOf(s).posts.find((x) => x.id === id);
  if (!p || !p.tagsYou || p.reshared || !p.img.startsWith('photo:')) return [];
  p.reshared = true;
  const photo = p.img.slice(6), soc = socialOf(s);
  const quality = Math.min(1, 0.55 + (p.npcLikes ?? p.likes) / 4000);
  const ev = postPhoto(s, { id: photo, caption: `📸 by @${p.author.handle}`, song: null, place: p.place, takenBy: p.author.name, quality });
  const mine = soc.posts.find((x) => x.id === photo);
  if (mine) mine.repostOf = { author: p.author.name, handle: p.author.handle };
  // the agency reposts you: more people see it, some follow
  const r = new Rng((s.seed ^ Math.imul(s.minutes + 3, 0x27d4eb2f)) >>> 0);
  if (NPC_REACTIONS) soc.queue.push({ at: s.minutes + r.int(20, 60), postId: photo, likes: r.int(80, 300), follows: r.int(15, 90), comment: { name: p.author.handle, text: r.pick(['Our star ✨', 'Killing it 🔥', '❤️❤️', 'Book them before we do 😉']), t: 0 } });
  else soc.queue.push({ at: s.minutes + r.int(20, 60), postId: photo, likes: 0, follows: r.int(15, 90) });
  s.inbox.push({ at: s.minutes + r.int(20, 60), from: p.author.name.split(',')[0], text: 'We reposted you! 🔥' });
  return ev;
}

/** what you might say under someone's post */
export function commentIdeas(s: LifeState): string[] { return COMMENTS[homeCountry(s)]; }
